import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMoon, FiSun } from 'react-icons/fi';

const BLOG_THEME_STORAGE_KEY = 'sr-blog-theme';

const getInitialBlogTheme = () => {
  if (typeof window === 'undefined') {
    return true;
  }

  const storedTheme = window.localStorage.getItem(BLOG_THEME_STORAGE_KEY);
  if (storedTheme === 'light') {
    return false;
  }

  return true;
};

function BlogPage() {
  const [isDark, setIsDark] = useState(getInitialBlogTheme);

  useEffect(() => {
    document.title = 'Supan Roy | Blogs';
  }, []);

  useEffect(() => {
    window.localStorage.setItem(BLOG_THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <div
      className={`min-h-screen font-sans transition-colors duration-500 ${
        isDark
          ? 'dark bg-gradient-to-b from-[#0a0f1c] via-[#0f172a] to-[#151f33]'
          : 'bg-gradient-to-b from-slate-50 via-white to-slate-100'
      }`}
    >
      <header className="sticky top-0 z-20 border-b border-slate-400/80 bg-slate-200/95 backdrop-blur dark:border-white/10 dark:bg-[#08101b]/92 dark:supports-[backdrop-filter]:bg-[#08101b]/80">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="transition hover:opacity-90" aria-label="Go to home page">
            <img src="/favicon.svg" alt="SR Logo" className="h-8 w-8 rounded-md" />
          </Link>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsDark((prev) => !prev)}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`rounded-full border p-2.5 transition ${
                isDark
                  ? 'border-amber-300/70 bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'border-slate-800/70 bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              {isDark ? <FiSun size={16} /> : <FiMoon size={16} />}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-5 pb-20 pt-12 sm:px-8 sm:pt-16">
        <section className="animate-fade-up">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300/90">Blog Index</p>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Engineering Blogs
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-700 dark:text-slate-200 sm:text-lg">
            This section includes technical blogs from practical software and infrastructure topics.
          </p>

          <Link
            to="/blogs/cadence-system-design"
            className="mt-8 block rounded-2xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-0.5 hover:border-purple-400 dark:border-white/10 dark:bg-[#0f172a]/80"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-700 dark:text-purple-300">Latest</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
              <img
                src="/cadence-logo.svg"
                alt="Cadence logo"
                className="mr-2 inline-block h-8 w-8 align-middle"
              />
              <span className="align-middle">Cadence: A System Design Deep Dive</span>
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold text-slate-900 dark:text-slate-100">Supan Roy</span>
              <span>15 Apr, 2026</span>
              <span>18:26</span>
            </div>
            <p className="mt-4 text-base leading-relaxed text-slate-700 dark:text-slate-200">
              Visual architecture guide covering modern distributed systems design, service topology, data flow patterns, and scaling strategies.
            </p>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.16em] text-purple-700 dark:text-purple-300">
              Read Blog
            </p>
          </Link>

          <Link
            to="/blogs/docker-guide"
            className="mt-6 block rounded-2xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-0.5 hover:border-sky-400 dark:border-white/10 dark:bg-[#0f172a]/80"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-400">Popular</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
              <img
                src="https://cdn.simpleicons.org/docker/2496ED"
                alt="Docker logo"
                className="mr-2 inline-block h-8 w-8 align-middle"
              />
              <span className="align-middle">Docker: From Fundamentals to Practical Usage</span>
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold text-slate-900 dark:text-slate-100">Supan Roy</span>
              <span>30 Mar, 2026</span>
              <span>23:11</span>
            </div>
            <p className="mt-4 text-base leading-relaxed text-slate-700 dark:text-slate-200">
              A complete beginner-to-practical walkthrough of Docker with daily command references and production deployment patterns.
            </p>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.16em] text-slate-600 dark:text-slate-400">
              Read Blog
            </p>
          </Link>

          <Link
            to="/blogs/cloudflare-tunnel-guide"
            className="mt-6 block rounded-2xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-0.5 hover:border-orange-400 dark:border-white/10 dark:bg-[#0f172a]/80"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-700 dark:text-orange-300">Archived</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
              <img
                src="https://cdn.simpleicons.org/cloudflare/F38020"
                alt="Cloudflare logo"
                className="mr-2 inline-block h-8 w-8 align-middle"
              />
              <span className="align-middle">Self-Hosting a Linux Server with Cloudflare Tunnel</span>
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold text-slate-900 dark:text-slate-100">Supan Roy</span>
              <span>10 Feb, 2026</span>
              <span>11:06</span>
            </div>
            <p className="mt-4 text-base leading-relaxed text-slate-700 dark:text-slate-200">
              Step-by-step self-hosting setup using Cloudflare Tunnel and Nginx with HTTPS and zero open ports.
            </p>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.16em] text-orange-700 dark:text-orange-300">
              Read Blog
            </p>
          </Link>
        </section>
      </main>
    </div>
  );
}

export default BlogPage;
